import { CommonModule } from '@angular/common'
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CreateThemeRequest, EmailTheme, UpdateThemeRequest } from '../../interfaces/email-theme.interface'
import { EmailComposerService } from '../../services/email-composer.service'

@Component({
  selector: 'ngx-theme-editor',
  templateUrl: './theme-editor.component.html',
  styleUrls: ['./theme-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule]
})
export class ThemeEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() themeId: number | null = null
  @Output() close = new EventEmitter<void>()
  @Output() saved = new EventEmitter<EmailTheme>()

  themeForm: FormGroup
  loading = false
  saving = false
  isEditMode = false

  // Common CSS properties for themes
  commonCssProperties = [
    { key: 'primary_color', label: 'Primary Color', type: 'color', defaultValue: '#667eea' },
    { key: 'secondary_color', label: 'Secondary Color', type: 'color', defaultValue: '#764ba2' },
    { key: 'text_color', label: 'Text Color', type: 'color', defaultValue: '#1f2937' },
    { key: 'background_color', label: 'Background Color', type: 'color', defaultValue: '#ffffff' },
    { key: 'font_family', label: 'Font Family', type: 'text', defaultValue: 'Arial, sans-serif' },
    { key: 'font_size', label: 'Font Size', type: 'text', defaultValue: '16px' },
    { key: 'line_height', label: 'Line Height', type: 'text', defaultValue: '1.5' },
    { key: 'text_alignment', label: 'Text Alignment', type: 'select', defaultValue: 'left', options: ['left', 'center', 'right', 'justify'] },
    { key: 'border_radius', label: 'Border Radius', type: 'text', defaultValue: '8px' },
    { key: 'max_width', label: 'Max Width', type: 'text', defaultValue: '600px' },
    { key: 'padding', label: 'Padding', type: 'text', defaultValue: '20px' }
  ]

  private destroy$ = new Subject<void>()

  constructor(private fb: FormBuilder, private emailComposerService: EmailComposerService) {
    this.themeForm = this.createForm()
  }

  ngOnInit(): void {
    this.checkEditMode()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['themeId']) {
      this.checkEditMode()
    }
  }

  private checkEditMode(): void {
    if (this.themeId) {
      this.isEditMode = true
      this.loadTheme()
    } else {
      this.isEditMode = false
      this.themeForm.reset()
      this.themeForm = this.createForm()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  private createForm(): FormGroup {
    const form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      slug: ['', [Validators.pattern(/^[a-z0-9\-_]*$/)]],
      description: ['', [Validators.maxLength(1000)]],
      is_active: [true],
      is_default: [false],
      global_css: this.fb.group({})
    })

    // Add CSS property controls
    const globalCssGroup = form.get('global_css') as FormGroup
    this.commonCssProperties.forEach((prop) => {
      globalCssGroup.addControl(prop.key, this.fb.control(prop.defaultValue))
    })

    return form
  }

  private loadTheme(): void {
    if (!this.themeId) return

    this.loading = true
    this.emailComposerService
      .getTheme(this.themeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Some APIs may not include a `success` flag. Prefer data when present.
          if ((response as any)?.data) {
            this.populateForm((response as any).data as EmailTheme)
          }
          this.loading = false
        },
        error: (error) => {
          console.error('Error loading theme:', error)
          this.loading = false
        }
      })
  }

  private populateForm(theme: EmailTheme): void {
    this.themeForm.patchValue({
      name: theme.name,
      slug: theme.slug,
      description: theme.description,
      is_active: theme.is_active,
      is_default: theme.is_default
    })

    // Populate CSS properties
    const globalCssGroup = this.themeForm.get('global_css') as FormGroup

    // Case 1: API returns key-value object in global_css
    if (theme.global_css && !Array.isArray((theme as any).global_css)) {
      this.commonCssProperties.forEach((prop) => {
        const value = (theme.global_css as any)[prop.key] || prop.defaultValue
        globalCssGroup.get(prop.key)?.setValue(value)
      })
      return
    }

    // Case 2: API returns CSS array and variables define the values
    // Map variables by slug to fill our controls
    const variableMap: Record<string, any> = {}
    if ((theme as any).variables && Array.isArray((theme as any).variables)) {
      ;((theme as any).variables as any[]).forEach((v: any) => {
        if (v?.slug) {
          variableMap[v.slug] = v.default_value
        }
      })
    }

    this.commonCssProperties.forEach((prop) => {
      const value = variableMap[prop.key] ?? prop.defaultValue
      globalCssGroup.get(prop.key)?.setValue(value)
    })
  }

  onSave(): void {
    if (this.themeForm.invalid) {
      this.markFormGroupTouched()
      return
    }

    this.saving = true
    const formValue = this.themeForm.value

    // Clean up global_css - remove empty values
    const cleanedGlobalCss: Record<string, any> = {}
    Object.keys(formValue.global_css).forEach((key) => {
      const value = formValue.global_css[key]
      if (value && value.trim() !== '') {
        cleanedGlobalCss[key] = value
      }
    })

    if (this.isEditMode && this.themeId) {
      const updateData: UpdateThemeRequest = {
        name: formValue.name,
        description: formValue.description,
        global_css: cleanedGlobalCss,
        is_active: formValue.is_active,
        is_default: formValue.is_default
      }

      this.emailComposerService
        .updateTheme(this.themeId, updateData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.saved.emit(response.data)
              this.close.emit()
            }
            this.saving = false
          },
          error: (error) => {
            console.error('Error updating theme:', error)
            this.saving = false
          }
        })
    } else {
      const createData: CreateThemeRequest = {
        name: formValue.name,
        slug: formValue.slug || undefined,
        description: formValue.description,
        global_css: cleanedGlobalCss,
        is_active: formValue.is_active,
        is_default: formValue.is_default
      }

      this.emailComposerService
        .createTheme(createData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.saved.emit(response.data)
              this.close.emit()
            }
            this.saving = false
          },
          error: (error) => {
            console.error('Error creating theme:', error)
            this.saving = false
          }
        })
    }
  }

  onCancel(): void {
    this.close.emit()
  }

  onGenerateSlug(): void {
    const name = this.themeForm.get('name')?.value
    if (name) {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9\s-_]/g, '') // Remove invalid characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/--+/g, '-') // Replace multiple hyphens with single
        .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
        .trim()
      this.themeForm.get('slug')?.setValue(slug)
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.themeForm.controls).forEach((key) => {
      const control = this.themeForm.get(key)
      control?.markAsTouched()

      if (control instanceof FormGroup) {
        this.markFormGroupTouched()
      }
    })
  }

  // Helper methods for template
  isFieldInvalid(fieldName: string): boolean {
    const field = this.themeForm.get(fieldName)
    return !!(field && field.invalid && (field.dirty || field.touched))
  }

  getFieldError(fieldName: string): string {
    const field = this.themeForm.get(fieldName)
    if (field?.errors) {
      if (field.errors['required']) return `${fieldName} is required`
      if (field.errors['maxlength']) return `${fieldName} is too long`
      if (field.errors['pattern'] && fieldName === 'slug') {
        return 'Slug can only contain lowercase letters, numbers, hyphens, and underscores'
      }
    }
    return ''
  }

  getPreviewGradient(): string {
    const globalCss = this.themeForm.get('global_css')?.value
    const primary = globalCss?.primary_color || '#667eea'
    const secondary = globalCss?.secondary_color || '#764ba2'
    return `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`
  }
}
